/**
 * The game's own confirmation dialog, in place of window.confirm: the same
 * modal the Extinction uses, so a question the game asks looks like part of
 * the game rather than the browser's. One dialog, reused; `ask` opens it and
 * resolves true for the confirm button, false for cancel, Escape or a click
 * outside it.
 */

export interface ConfirmArt {
  src: string;
  /** Native size of the art, drawn 1:1 so the pixels stay square. */
  size: number;
  label: string;
}

export interface ConfirmOptions {
  title: string;
  body: string;
  note?: string;
  confirm: string;
  cancel?: string;
  /** Colours the border and the confirm button: violet for an evolution. */
  tone?: 'evolve';
  /** Pictures shown in a row with arrows between them: a before and after. */
  art?: readonly ConfirmArt[];
}

export type Ask = (options: ConfirmOptions) => Promise<boolean>;

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

export function createConfirm(root: HTMLElement): Ask {
  const backdrop = el('div', 'modal');
  backdrop.hidden = true;
  const panel = el('div', 'modal__panel confirm');
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-modal', 'true');
  const title = el('h2', 'modal__title');
  const art = el('div', 'confirm__art');
  const body = el('p', 'modal__body');
  const note = el('p', 'modal__note');
  const actions = el('div', 'modal__actions');
  const yes = el('button', 'modal__button');
  yes.type = 'button';
  const no = el('button', 'modal__button modal__button--quiet');
  no.type = 'button';
  actions.append(yes, no);
  panel.append(title, art, body, note, actions);
  backdrop.append(panel);
  root.append(backdrop);

  let settle: ((ok: boolean) => void) | null = null;
  let returnFocus: HTMLElement | null = null;

  function finish(ok: boolean): void {
    if (!settle) return;
    const done = settle;
    settle = null;
    backdrop.hidden = true;
    returnFocus?.focus();
    done(ok);
  }

  yes.addEventListener('click', () => finish(true));
  no.addEventListener('click', () => finish(false));
  backdrop.addEventListener('click', (ev) => {
    if (ev.target === backdrop) finish(false);
  });
  // The confirm sits over everything else (the dinosaur card, a held item,
  // the footer panels), and each of those also answers Escape. Listening in
  // the capture phase runs this first; stopping the event there means one
  // Escape says "no" to the question and nothing more.
  window.addEventListener(
    'keydown',
    (ev) => {
      if (!settle || ev.key !== 'Escape') return;
      ev.stopPropagation();
      ev.stopImmediatePropagation();
      finish(false);
    },
    true,
  );

  return (options) => {
    // A second question while one is open answers the first with no.
    finish(false);
    title.textContent = options.title;
    body.textContent = options.body;
    note.textContent = options.note ?? '';
    note.hidden = !options.note;
    yes.textContent = options.confirm;
    no.textContent = options.cancel ?? 'Cancel';
    const evolve = options.tone === 'evolve';
    panel.classList.toggle('confirm--evolve', evolve);
    yes.classList.toggle('modal__button--evolve', evolve);
    art.replaceChildren();
    for (const [i, a] of (options.art ?? []).entries()) {
      if (i > 0) art.append(el('span', 'confirm__arrow', '→'));
      const fig = el('figure', 'confirm__figure');
      const img = el('img', 'confirm__img');
      img.src = a.src;
      img.alt = '';
      img.width = a.size;
      img.height = a.size;
      fig.append(img, el('figcaption', 'confirm__caption', a.label));
      art.append(fig);
    }
    art.hidden = art.childElementCount === 0;
    returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    backdrop.hidden = false;
    yes.focus();
    return new Promise<boolean>((resolve) => {
      settle = resolve;
    });
  };
}
